import type { Etapa } from '@plane/shared';

import { TituloComDestaque } from '@/components/titulo-com-destaque';
import { useProcesso } from '@/hooks/use-conteudo';
import { useRevelarAoRolar } from '@/hooks/use-revelar-ao-rolar';

function revelacao(visivel: boolean): string {
  return `transition-[opacity,transform] duration-[900ms] ease-plane motion-reduce:transition-none ${
    visivel ? 'translate-y-0 opacity-100' : 'translate-y-[26px] opacity-0'
  }`;
}

/**
 * Uma etapa.
 *
 * Cada uma observa a si mesma, e não a seção. É o comportamento do protótipo, e
 * a diferença aparece no celular: em coluna única, um observador de seção só
 * revelaria as quatro de uma vez, e as três de baixo terminariam a animação
 * fora da tela.
 *
 * O traço verde que cresce sobre a borda é o `::after` do original. Aqui é um
 * elemento de verdade porque a largura precisa reagir ao estado do React, e
 * pseudo-elemento não recebe classe condicional.
 */
function EtapaItem({ etapa, numero }: { etapa: Etapa; numero: string }) {
  const { ref, visivel } = useRevelarAoRolar<HTMLLIElement>();

  return (
    <li ref={ref} className={`relative border-t border-clay pt-[26px] ${revelacao(visivel)}`}>
      <span
        aria-hidden="true"
        className={`absolute top-[-1px] left-0 h-px bg-brand-600 transition-[width] delay-150 duration-1000 ease-plane-slow motion-reduce:transition-none ${
          visivel ? 'w-full' : 'w-0'
        }`}
      />

      {/* O `<ol>` já numera para quem usa leitor de tela; repetir viraria "1, 01". */}
      <span
        aria-hidden="true"
        className="mb-[1.4rem] block text-[0.68rem] tracking-[0.2em] text-brass"
      >
        {numero}
      </span>

      <h3 className="mb-[0.6em] font-display text-[clamp(1.2rem,1.8vw,1.55rem)] tracking-[-0.015em] text-brand-950">
        {etapa.titulo}
      </h3>

      <p className="mb-[1.2em] text-[0.92rem] leading-[1.6] text-ink-soft">{etapa.descricao}</p>

      {etapa.prazo !== '' && (
        <span className="inline-block border border-brand-150 px-[0.8em] py-[0.4em] text-[0.64rem] tracking-[0.16em] text-brand-600 uppercase">
          {etapa.prazo}
        </span>
      )}
    </li>
  );
}

/**
 * Seção 04 — Processo.
 *
 * Portada de `legacy/static-site/index.html`, seção `#processo`. É a única até
 * aqui que não depende de foto nenhuma, então veio inteira.
 *
 * A grade acompanha os dois pontos de quebra do protótipo: quatro colunas,
 * duas abaixo de 980 px e uma abaixo de 640 px.
 */
export function ProcessoSection() {
  const { ref, visivel } = useRevelarAoRolar<HTMLElement>();
  const processo = useProcesso();

  return (
    <section
      ref={ref}
      id="processo"
      className="bg-bone py-[clamp(70px,10vw,140px)]"
      aria-labelledby="processo-titulo"
    >
      <div className="mx-auto w-full max-w-[1360px] px-6">
        <header className={`mb-[clamp(44px,6vw,84px)] ${revelacao(visivel)}`}>
          <p className="flex items-center gap-2.5 text-[0.72rem] font-medium tracking-[0.22em] text-brand-600 uppercase">
            <span
              aria-hidden="true"
              className="inline-block h-px w-[34px] bg-brand-600 opacity-75"
            />
            {processo.kicker}
          </p>

          <TituloComDestaque
            id="processo-titulo"
            texto={processo.titulo}
            className="mt-[1.4rem] font-display text-[clamp(2.1rem,5.4vw,4.4rem)] leading-[0.99] tracking-[-0.025em] text-brand-950"
            destaqueClassName="font-light text-brand-600 italic"
          />
        </header>

        <ol className="m-0 grid list-none grid-cols-1 gap-[26px] p-0 min-[640px]:grid-cols-2 min-[640px]:gap-8 min-[980px]:grid-cols-4 min-[980px]:gap-[clamp(20px,2.4vw,36px)]">
          {processo.etapas.map((etapa, indice) => (
            <EtapaItem
              key={etapa.titulo}
              etapa={etapa}
              // A numeração sai da posição, e não de um campo: assim ela nunca
              // contradiz a ordem da lista.
              numero={String(indice + 1).padStart(2, '0')}
            />
          ))}
        </ol>
      </div>
    </section>
  );
}
